import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import currency from "currency.js";
import { PriceDetailViewProps } from "./types";
import LabelView from "../LabelView";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { CustomtOption, PdpSections } from "@utils/customization";
import { CustomisationKey } from "@scenes/pdp/types";
import {
  getFilterDetailData,
  isLooseParcel,
  isPair,
  isParcel,
  isMeleeItem,
} from "@utils/product/productValidators";
import { calculatedWidth } from "./utils";
import { useFilterSections } from "../../../hooks/useFilteredOptions";
import { PriceModes } from "../PriceModeCard/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { css } from "styled-components/native";
import { ProductCategory } from "@utils/constants";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useSelector } from "react-redux";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { productLevelOneColor } from "@utils/product/productTheme";

const PriceDetailView: React.FC<PriceDetailViewProps> = ({
  itemDetail,
  priceMode,
  productType,
  expandView,
  fromMatchPair,
}) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const userInfo = useUserInfo();
  const userCurrency = useUserCurrency();
  const { t } = useTranslation();
  const hasTwoItems = () => {
    if (Object.keys(filteredOptions).length === 0) {
      return mainOptions?.length === 2;
    } else {
      const subsectionsWithItems = Object.values(filteredOptions).filter(
        (options) => options.length > 0
      );
      return (
        subsectionsWithItems?.length === 1 &&
        subsectionsWithItems?.[0].length === 2
      );
    }
  };
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);

  const stoneType = isPair(itemDetail)
    ? SearchFilterTypeEnum.PAIRS
    : isCustomApp && isParcel(itemDetail)
    ? SearchFilterTypeEnum.PARCEL
    : SearchFilterTypeEnum.SINGLE_STONES;
  const { deviceWidth } = useIsFoldable();
  const screenWidth = !theme.isMobile ? 400 : deviceWidth;
  const screenDivision = screenWidth / (itemDetail?.is_fancy ? 3 : 4);

  const sectionLayout = useGetPdpSectionLayout(
    PdpSections.SUMMARY,
    productType,
    stoneType
  );
  const [mainOptions, setMainOptions] = useState<CustomtOption[]>([]);

  const objReportIssue = getFilterDetailData(
    CustomisationKey.pricing_section,
    sectionLayout
  );

  const shouldDisplayRows = objReportIssue?.[0]?.displayRows || false;

  useEffect(() => {
    if (objReportIssue && objReportIssue?.[0]?.options) {
      const mainSectionOptions = Object.values(objReportIssue?.[0]?.options);
      const filteredMainOptions = transformPriceConfig(
        mainSectionOptions,
        isCustomApp && itemDetail?.special_price !== null
      )
        .filter((option: any) => {
          const hasPriceModes =
            Array.isArray(option.price_modes) &&
            option.price_modes.includes(priceMode);

          if (
            itemDetail?.is_fancy &&
            option.name === CustomisationKey.discount_percent
          ) {
            return false;
          }

          if (
            isCustomApp &&
            isLooseParcel(itemDetail) &&
            ((option.name === CustomisationKey.price_per_carat &&
              itemDetail?.hide_price_per_carat) ||
              (option.name === CustomisationKey.total_sales_price &&
                itemDetail?.hide_total_sales_price))
          ) {
            return false;
          }

          return hasPriceModes;
        })
        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

      setMainOptions(filteredMainOptions);
    }
  }, [objReportIssue && objReportIssue?.[0]?.options, priceMode]);

  const transformPriceConfig = (config: any[], hasSpecialPrice: boolean) => {
    if (hasSpecialPrice) {
      // remove total_sales_price
      const filtered = config.filter(
        (item) => item.name !== "total_sales_price"
      );

      return [...filtered];
    }

    // hasSpecialPrice === false
    return config.filter(
      (item) =>
        item.name !== "special_price" && item.name !== "retail_price_special"
    );
  };

  const filteredOptions = useFilterSections(
    theme,
    priceMode,
    objReportIssue?.[0]
  );

  const hasInclusionOpenInConfig = useMemo(() => {
    if (
      mainOptions.some((opt) => opt.name === CustomisationKey.inclusion_open)
    ) {
      return true;
    }

    if (Object.keys(filteredOptions).length > 0) {
      return Object.values(filteredOptions).some((options) =>
        options.some(
          (opt: CustomtOption) => opt.name === CustomisationKey.inclusion_open
        )
      );
    }

    return false;
  }, [mainOptions, filteredOptions]);

  const isInclusionOpenInvalid = useMemo(() => {
    return (
      hasInclusionOpenInConfig &&
      (itemDetail?.inclusion_open === null || itemDetail?.inclusion_open === "")
    );
  }, [hasInclusionOpenInConfig, itemDetail?.inclusion_open]);

  const renderPriceDetail = (option: CustomtOption, index: number) => {
    const itemDetailKey = option.name as CustomisationKey;

    const isFirstItem = index === 0;
    const twoItems = hasTwoItems();
    const maxWidth = shouldDisplayRows
      ? "100%"
      : isFirstItem
      ? twoItems
        ? "40%"
        : theme.isLargeScreen
        ? "20%"
        : "28%"
      : "100%";

    const extraCss = option.extra_css || {};
    const customTypography = extraCss.typography
      ? typography[extraCss.typography]
      : undefined;
    const textDecoration = extraCss.textDecoration || undefined;

    let itemPriceValue = "0";
    if (
      [
        CustomisationKey.price_per_carat,
        CustomisationKey.discount_percent,
      ].includes(itemDetailKey)
    ) {
      itemPriceValue = itemDetail?.[option.response_key];
    }

    let itemLeftText = "";
    let itemRightText = "";
    let strikeThrough = false;
    let specialPrice = false;

    switch (itemDetailKey) {
      case CustomisationKey.price:
        itemLeftText = "";
        itemRightText = t(option.label);
        break;
      case CustomisationKey.retail_price_name:
        itemLeftText = "";
        itemRightText = t(option.label);
        break;
      case CustomisationKey.retail_price_per_carat:
        itemLeftText = t(option.label);
        itemRightText =
          itemPriceValue === null ||
          itemPriceValue === "0" ||
          itemPriceValue === "0.0"
            ? "-"
            : currency(itemPriceValue, {
                symbol: userCurrency,
                precision: 0,
              }).format();
        break;
      case CustomisationKey.price_per_carat:
        itemLeftText = t(option.label);
        itemRightText =
          itemPriceValue === null ||
          itemPriceValue === "0" ||
          itemPriceValue === "0.0"
            ? "-"
            : currency(itemPriceValue, {
                symbol: userCurrency,
                precision: 0,
              }).format();
        break;
      case CustomisationKey.discount_percent:
        itemLeftText = t(option.label);
        itemRightText =
          itemPriceValue === null ||
          itemPriceValue === "0" ||
          itemPriceValue === "0.0"
            ? "-"
            : `${currency(itemPriceValue, {
                symbol: "",
                precision: 2,
              }).format()}%`;
        break;
      case CustomisationKey.total_sales_price:
        if (isInclusionOpenInvalid) {
          itemLeftText = t("searchResult.summaryBox.totalPrice.text");
        } else {
          itemLeftText = t(option.label);
        }
        itemRightText =
          itemDetail?.[itemDetailKey] === null
            ? "-"
            : currency(itemDetail?.[option.response_key], {
                symbol: userCurrency,
                precision:
                  userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
              }).format();
        break;
      case CustomisationKey.retail_price:
        itemLeftText = t(option.label);
        itemRightText =
          itemDetail?.[itemDetailKey] === null
            ? "-"
            : currency(itemDetail?.[option.response_key], {
                symbol: userCurrency,
                precision:
                  userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
              }).format();
        break;
      case CustomisationKey.rapnet_price:
        itemLeftText = t(option.label);
        itemRightText =
          itemDetail?.[itemDetailKey] === null
            ? "-"
            : currency(itemDetail?.[option.response_key], {
                symbol: userCurrency,
                precision:
                  userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
              }).format();
        break;
      case CustomisationKey.inclusion_open:
        if (
          itemDetail?.[itemDetailKey] === null ||
          itemDetail?.[itemDetailKey] === ""
        ) {
          return null;
        }
        itemLeftText = t(option.label);
        itemRightText = currency(itemDetail?.[option.response_key], {
          symbol: userCurrency,
          precision:
            userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
        }).format();
        break;

      case CustomisationKey.retail_price_special:
        itemLeftText = t(option.label);
        itemRightText =
          itemDetail?.[itemDetailKey] === null
            ? "-"
            : currency(itemDetail?.special_price, {
                symbol: userCurrency,
                precision:
                  userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
              }).format();
        strikeThrough = true;
        break;

      case CustomisationKey.special_price:
        itemLeftText = t(option.label);
        itemRightText =
          itemDetail?.[itemDetailKey] === null
            ? "-"
            : currency(itemDetail?.total_sales_price, {
                symbol: userCurrency,
                precision:
                  userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
              }).format();
        specialPrice = true;
        break;

      default:
        if (
          itemDetail?.[itemDetailKey] === null ||
          itemDetail?.[itemDetailKey] === ""
        ) {
          return null;
        }
        itemLeftText = t(option.label);
        itemRightText =
          itemDetail?.[itemDetailKey] === null
            ? "-"
            : itemDetail?.[option.response_key];
    }

    if (shouldDisplayRows) {
      return (
        <CustomView key={option.name}>
          <LabelView
            itemLeftText={itemLeftText}
            itemRightText={itemRightText}
            ellipsize={false}
            rightPrefixTextTheme={theme["core-05"]}
            leftPostfixText=""
            itemLeftTextTheme={theme["core-06"]}
            isFixedKeyWidth={true}
            cellWidth={theme.isMobile ? 250 : 200}
            keyTypographyStyle={typography["Body-R"]}
            valueTypographyStyle={
              (itemDetailKey === CustomisationKey.total_sales_price &&
                isInclusionOpenInvalid) ||
              specialPrice
                ? typography["H1-B"]
                : customTypography || typography["Body-M"]
            }
            keyWidthPercent={!theme.isLargeScreen ? 40 : undefined}
            keyWidth={theme.isLargeScreen ? 120 : 50}
            marginRight={theme.isMobile ? 50 : 0}
            isFixWidth
            valueTextDecoration={
              itemDetailKey === CustomisationKey.total_sales_price &&
              isInclusionOpenInvalid
                ? undefined
                : textDecoration
            }
            strikeThrough={strikeThrough}
            specialPrice={specialPrice}
            {...(specialPrice && {
              rightIconSpecial: VDBIcons.PERCENTAGE_CIRCLE,
              rightIconSizeSpecial: 16,
              rightIconThemeSpecial: theme["main-04"],
            })}
          />
        </CustomView>
      );
    }

    return (
      <LabelTextContainer
        key={option.name}
        maxWidth={maxWidth}
        isFirstItem={!isFirstItem}
        displayRows={shouldDisplayRows}
      >
        <LabelView
          itemLeftText={itemLeftText}
          itemRightText={itemRightText}
          direction="column"
          cellWidth={
            calculatedWidth(screenDivision, option.name) + SpaceType.Double
          }
          keyWidth={calculatedWidth(screenDivision, option.name)}
          {...(itemLeftText && { keyMarginBottom: 12 })}
          valueTypographyStyle={
            itemDetailKey === CustomisationKey.total_sales_price &&
            isInclusionOpenInvalid
              ? typography["H1-B"]
              : customTypography ||
                (index === 0 ? typography["Body-R"] : typography["Body-M"])
          }
          rightPrefixTextTheme={
            index === 0 ? theme["core-06"] : theme["core-05"]
          }
          ellipsize={false}
          valueTextDecoration={
            itemDetailKey === CustomisationKey.total_sales_price &&
            isInclusionOpenInvalid
              ? undefined
              : textDecoration
          }
        />
      </LabelTextContainer>
    );
  };

  return (
    <PriceViewContainer>
      {Object.keys(filteredOptions).length === 0 ? (
        mainOptions.length > 0 && (
          <MainContainer
            fromMatchPair={
              (itemDetail?.pricing_comment || fromMatchPair) ?? false
            }
            displayRows={shouldDisplayRows}
          >
            <OptionContainer displayRows={shouldDisplayRows}>
              {mainOptions.map((option, index) => {
                return renderPriceDetail(option, index);
              })}
            </OptionContainer>
          </MainContainer>
        )
      ) : (
        <MainContainer
          fromMatchPair={
            (itemDetail?.pricing_comment || fromMatchPair) ?? false
          }
          displayRows={shouldDisplayRows}
        >
          {Object.entries(filteredOptions).length > 0 &&
            Object.entries(filteredOptions).map(
              ([key, options]) =>
                options.length > 0 && (
                  <OptionContainer key={key} displayRows={shouldDisplayRows}>
                    {options.map((option, index) =>
                      renderPriceDetail(option, index)
                    )}
                  </OptionContainer>
                )
            )}
        </MainContainer>
      )}

      {itemDetail?.pricing_comment &&
        !fromMatchPair &&
        priceMode !== PriceModes.ORANGE && (
          <>
            <Divider />
            <LabelContainer>
              <VDBIcon
                color={
                  theme[productLevelOneColor(productType as ProductCategory)]
                }
                size={24}
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.PRICE_COMMENT}
              />
              <VDBText
                type={typography["Body-R"]}
                color={theme["core-06"]}
                numberOfLines={expandView ? undefined : 1}
                ellipsizeMode="tail"
              >
                {itemDetail?.pricing_comment}
              </VDBText>
            </LabelContainer>
          </>
        )}
    </PriceViewContainer>
  );
};

const MainContainer = styled.View<{
  fromMatchPair: boolean;
  displayRows?: boolean;
}>`
  flex-direction: column;
  padding: ${({ displayRows }) => (displayRows ? "16px" : "8px")};
  gap: 12px;
  border-radius: 8px;
  ${({ fromMatchPair }) =>
    !fromMatchPair &&
    css`
      border-width: 1px;
    `}
  background-color: ${({ theme }) => theme["core-01"]};
  border-color: ${({ theme }) => theme["core-03"]};
`;

const OptionContainer = styled.View<{
  displayRows?: boolean;
}>`
  flex-direction: ${({ displayRows }) => (displayRows ? "column" : "row")};
  ${({ displayRows }) =>
    displayRows &&
    css`
      gap: 0;
    `}
`;

const LabelTextContainer = styled.View<{
  maxWidth: string;
  isFirstItem: boolean;
  displayRows?: boolean;
}>`
  flex: 1;
  flex-direction: ${({ displayRows }) =>
    displayRows ? "row" : "column-reverse"};
  max-width: ${({ maxWidth }) => (maxWidth ? maxWidth : "100%")};
  ${({ isFirstItem, displayRows }) =>
    !displayRows &&
    isFirstItem &&
    css`
      justify-content: center;
    `}
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

const LabelContainer = styled.View`
  padding-top: 10px;
  padding-bottom: 3px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${SpaceType.Small}px;
  padding-horizontal: ${SpaceType.Basic}px;
`;

const PriceViewContainer = styled.View`
  align-content: flex-start;
`;

const CustomView = styled.View`
  justify-content: flex-start;
  margin-vertical: 8px;
`;

export default PriceDetailView;
